import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Download, ExternalLink, Zap, TrendingUp, Search, Sparkles, ShieldAlert, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicShare() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        axios.get(`${API}/public/shares/${token}`)
            .then((r) => setData(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Unable to load report"));
    }, [token]);

    if (error) return (
        <div className="min-h-screen grid place-items-center bg-background text-foreground">
            <div className="border border-destructive/50 p-8 max-w-md">
                <div className="overline text-destructive">Error</div>
                <div className="mt-3 text-lg">{error}</div>
                <div className="mt-4 text-xs text-muted-foreground">The link may have expired or been revoked by the report owner.</div>
            </div>
        </div>
    );
    if (!data) return <div className="min-h-screen grid place-items-center text-muted-foreground font-mono text-sm">loading report…</div>;

    const brand = data.share;
    const accent = brand.brand_color || "#38CFFF";
    const k = data.kpis;
    const a = data.audit;

    return (
        <div className="min-h-screen bg-background text-foreground" data-testid="public-share-page">
            {/* Branded header */}
            <header className="border-b border-border" style={{ background: "linear-gradient(180deg, #0a0a0a 0%, #0f0f0f 100%)" }}>
                <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                        {brand.logo_url ? (
                            <img src={brand.logo_url} alt={brand.brand_name} className="h-10 w-auto" />
                        ) : (
                            <span className="w-3 h-3 shrink-0" style={{ background: accent, boxShadow: `0 0 20px ${accent}80` }} />
                        )}
                        <div>
                            <div className="font-display text-2xl tracking-tight" data-testid="brand-name">{brand.brand_name}</div>
                            <div className="text-xs text-muted-foreground font-mono">SEO performance report</div>
                        </div>
                    </div>
                    <a href={`${API}/public/shares/${token}/report.pdf`} target="_blank" rel="noreferrer"
                       data-testid="download-pdf-btn"
                       className="px-4 py-2 text-sm font-medium flex items-center gap-2"
                       style={{ background: accent, color: "#0A0A0A" }}>
                        <Download className="w-4 h-4" /> Download PDF
                    </a>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-10 space-y-10">
                {/* Title */}
                <div>
                    <div className="overline">Prepared for</div>
                    <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">
                        {data.project.name}
                    </h1>
                    <div className="text-sm text-muted-foreground mt-2 font-mono">
                        {data.project.domain} · report generated {new Date(data.generated_at).toLocaleString()}
                    </div>
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                        [Zap, "Visibility", `${k.visibility}%`, k.visibility_delta],
                        [TrendingUp, "Est. Traffic", k.estimated_traffic.toLocaleString(), k.traffic_delta],
                        [Search, "Keywords", k.total_keywords, null],
                        [Sparkles, "AI Overviews", k.ai_overviews, null],
                    ].map(([Icon, label, val, delta]) => (
                        <div key={label} className="border border-border p-6 bg-card/40">
                            <div className="flex items-center justify-between">
                                <div className="overline">{label}</div>
                                <Icon className="w-4 h-4" style={{ color: accent }} strokeWidth={1.5} />
                            </div>
                            <div className="font-mono text-3xl mt-4">{val}</div>
                            {delta !== null && delta !== undefined && (
                                <div className={`text-xs font-mono mt-2 ${delta > 0 ? "text-success" : "text-destructive"}`}>
                                    {delta > 0 ? "+" : ""}{delta}%
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Trend + alerts */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2 border border-border p-6 bg-card/40">
                        <div className="overline mb-6">Visibility · 30 days</div>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={data.trend_30d}>
                                    <defs>
                                        <linearGradient id="pub-vis" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={accent} stopOpacity={0.6} />
                                            <stop offset="100%" stopColor={accent} stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid stroke="#1a1a1a" vertical={false} />
                                    <XAxis dataKey="day" stroke="#555" fontSize={10} />
                                    <YAxis stroke="#555" fontSize={10} />
                                    <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #2a2a2a", borderRadius: 0, fontSize: 12 }}
                                             itemStyle={{ color: accent }} />
                                    <Area type="monotone" dataKey="visibility" stroke={accent} strokeWidth={1.5} fill="url(#pub-vis)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="border border-border p-6 bg-card/40">
                        <div className="overline mb-4">Alerts</div>
                        <div className="space-y-3">
                            {data.alerts.length === 0
                                ? <div className="text-sm text-muted-foreground">All clear.</div>
                                : data.alerts.map((al) => (
                                    <div key={al.id} className={`border-l-2 pl-3 py-1 ${al.severity === "error" ? "border-destructive text-destructive" : al.severity === "warning" ? "border-warning text-warning" : "border-secondary text-secondary"}`}>
                                        <div className="text-sm font-medium">{al.title}</div>
                                        <div className="text-xs text-muted-foreground mt-0.5">{al.body}</div>
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>

                {/* Site health */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="border border-border p-6 bg-card/40">
                        <div className="flex items-center gap-2 mb-3">
                            <ShieldAlert className="w-4 h-4" style={{ color: accent }} />
                            <div className="overline">Site Health</div>
                        </div>
                        <div className="font-mono text-4xl">{a.health_score}<span className="text-base text-muted-foreground">/100</span></div>
                        <div className="mt-4 space-y-1 text-sm">
                            <div>Errors: <span className="font-mono text-destructive">{a.errors}</span></div>
                            <div>Warnings: <span className="font-mono text-warning">{a.warnings}</span></div>
                            <div>Notices: <span className="font-mono text-secondary">{a.notices}</span></div>
                        </div>
                    </div>
                    <div className="border border-border p-6 bg-card/40">
                        <div className="overline mb-3">Core Web Vitals</div>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between"><span>LCP</span><span className="font-mono">{a.core_web_vitals.lcp}s</span></div>
                            <div className="flex justify-between"><span>CLS</span><span className="font-mono">{a.core_web_vitals.cls}</span></div>
                            <div className="flex justify-between"><span>INP</span><span className="font-mono">{a.core_web_vitals.inp}ms</span></div>
                            <div className="flex justify-between"><span>Mobile</span><span className="font-mono">{a.core_web_vitals.mobile_score}/100</span></div>
                            <div className="flex justify-between"><span>Desktop</span><span className="font-mono">{a.core_web_vitals.desktop_score}/100</span></div>
                        </div>
                    </div>
                    <div className="border border-border p-6 bg-card/40">
                        <div className="flex items-center gap-2 mb-3">
                            <AlertTriangle className="w-4 h-4 text-destructive" />
                            <div className="overline">Backlinks</div>
                        </div>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between"><span>Total</span><span className="font-mono">{data.backlinks.backlinks.toLocaleString()}</span></div>
                            <div className="flex justify-between"><span>Ref. Domains</span><span className="font-mono">{data.backlinks.referring_domains.toLocaleString()}</span></div>
                            <div className="flex justify-between"><span>Domain Rating</span><span className="font-mono">{data.backlinks.domain_rating}</span></div>
                            <div className="flex justify-between"><span>Toxic</span><span className="font-mono text-destructive">{data.backlinks.toxic_domains}</span></div>
                        </div>
                    </div>
                </div>

                {/* Top movers */}
                <div className="border border-border bg-card/40">
                    <div className="p-6 border-b border-border">
                        <div className="overline">Top Movers</div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-xs text-muted-foreground">
                                <tr className="border-b border-border">
                                    <th className="text-left p-3">Keyword</th>
                                    <th className="text-right p-3">Position</th>
                                    <th className="text-right p-3">Change</th>
                                    <th className="text-right p-3">Volume</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.top_movers.map((m, i) => (
                                    <tr key={i} className="border-b border-border/50">
                                        <td className="p-3 font-mono">{m.keyword || "—"}</td>
                                        <td className="p-3 text-right font-mono">{m.position}</td>
                                        <td className={`p-3 text-right font-mono ${m.change > 0 ? "text-success" : m.change < 0 ? "text-destructive" : "text-muted-foreground"}`}>
                                            {m.change > 0 ? "+" : ""}{m.change}
                                        </td>
                                        <td className="p-3 text-right font-mono text-muted-foreground">{m.volume.toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>

            <footer className="border-t border-border py-8 mt-16">
                <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-6 items-center text-xs text-muted-foreground">
                    <div>{brand.footer_note || `© ${new Date().getFullYear()} ${brand.brand_name}`}</div>
                    <div className="text-center md:text-left space-y-1">
                        <div><a href="tel:+18552742732" className="hover:text-foreground">1-855-2PHASE2</a></div>
                        <div><a href="mailto:contact@phase2labs.net" className="hover:text-foreground">contact@phase2labs.net</a> · <a href="https://www.phase2labs.net" target="_blank" rel="noreferrer" className="hover:text-foreground">phase2labs.net</a></div>
                    </div>
                    <div className="text-right space-y-1">
                        <div className="font-mono">Link expires {new Date(brand.expires_at).toLocaleDateString()}</div>
                        <a href={brand.referral_code ? `/?ref=${brand.referral_code}` : "/"}
                           data-testid="powered-by-link"
                           className="inline-flex items-center gap-1 hover:text-foreground">
                            Powered by InPhase <ExternalLink className="w-3 h-3" />
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
}
